import { useState, useMemo, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  Mail,
  MessageSquare,
  Filter,
  ChevronLeft,
  ChevronRight,
  Building2,
  Clock,
  Search,
  ThumbsUp,
  ThumbsDown,
  Minus,
  Calendar,
  RefreshCw,
  XCircle,
  Loader2,
  Edit2,
  CheckCircle2,
  Sparkles,
  RotateCcw,
  Eye,
  AlertCircle,
  Tag,
  X,
  // Plus,  // Hidden for now - tags section disabled
  Share2,
  Send,
  LayoutGrid,
  List,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Trash2,
} from 'lucide-react'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Label,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Textarea,
  Checkbox,
  CallPreviewHoverCard,
} from '@/components/ui'
import { conversationsApi } from '@/api/endpoints/conversations'
import { emailQueueApi, type EmailQueueItem } from '@/api/endpoints/queue'
import type { ConversationFilters, ConversationType, Sentiment } from '@/types'
import { cn, formatDate, formatTime } from '@/lib/utils'
import { AudioWavePlayer } from '@/components/common/AudioWavePlayer'

// Extended Conversation type to match actual API response
interface ApiConversation {
  id: string | number
  type: 'email' | 'call' | 'outbound_call' | 'inbound_call' | 'advisor'  // API uses 'call', legacy uses 'outbound_call'
  timestamp: string | null
  created_at?: string
  subject: string | null
  summary: string | null  // API returns summary, not content
  transcript: string | null  // API returns string, not array
  sentiment: Sentiment | null
  intent?: string | null
  intent_detected?: string | null
  urgency?: string | null
  lead?: { id: number; company_name: string } | null
  lead_company?: string | null
  lead_phone?: string | null
  lead_email?: string | null
  caller_phone?: string | null
  recording_url?: string | null
  duration?: number | null  // API returns duration, not duration_seconds
  answered_by?: string | null
  tags?: string[]
  transferred?: boolean
  ai_responded?: boolean
  response_count?: number
  primary_intent?: string | null
}

// Note: EmailQueueItem, EmailQueueStats, and EmailQueueItemWithContext are imported from '@/api/endpoints/queue'

const CONVERSATION_TYPES = [
  { value: 'calls', label: 'All Calls' },  // Combined outbound + inbound calls (default)
  { value: 'all', label: 'All Types' },
  { value: 'email', label: 'Emails' },
  { value: 'call', label: 'Outbound Calls' },  // API uses 'call' not 'outbound_call'
  { value: 'inbound_call', label: 'Inbound Calls' },
  { value: 'advisor', label: 'Advisor' },
]

const SENTIMENTS = [
  { value: 'all', label: 'All Sentiments' },
  { value: 'positive', label: 'Positive' },
  { value: 'neutral', label: 'Neutral' },
  { value: 'negative', label: 'Negative' },
]

const ANSWERED_BY = [
  { value: 'all', label: 'All' },
  { value: 'human', label: 'Human' },
  { value: 'voicemail', label: 'Voicemail' },
  { value: 'ivr', label: 'IVR' },
  { value: 'unknown', label: 'Unknown' },
]

const PAGE_SIZES = [10, 25, 50]

// Type helpers - handle both 'call' (API) and 'outbound_call' (legacy)
type ApiConversationType = 'email' | 'call' | 'outbound_call' | 'inbound_call' | 'advisor'

const getTypeIcon = (type: ApiConversationType) => {
  if (type === 'email') return Mail
  if (type === 'advisor') return MessageSquare
  if (type === 'inbound_call') return PhoneIncoming
  if (type === 'call' || type === 'outbound_call') return PhoneOutgoing
  return Phone
}

const getTypeColor = (type: ApiConversationType) => {
  switch (type) {
    case 'email':
      return 'bg-blue-100 text-blue-700 border-blue-200'
    case 'call':
    case 'outbound_call':
      return 'bg-green-100 text-green-700 border-green-200'
    case 'inbound_call':
      return 'bg-teal-100 text-teal-700 border-teal-200'
    case 'advisor':
      return 'bg-purple-100 text-purple-700 border-purple-200'
    default:
      return 'bg-gray-100 text-gray-700 border-gray-200'
  }
}

const getTypeBadgeColor = (type: ApiConversationType) => {
  switch (type) {
    case 'email':
      return 'bg-blue-500'
    case 'call':
    case 'outbound_call':
      return 'bg-green-500'
    case 'inbound_call':
      return 'bg-teal-500'
    case 'advisor':
      return 'bg-purple-500'
    default:
      return 'bg-gray-500'
  }
}

const getSentimentIcon = (sentiment: Sentiment | null) => {
  switch (sentiment) {
    case 'positive':
      return ThumbsUp
    case 'negative':
      return ThumbsDown
    default:
      return Minus
  }
}

const getSentimentColor = (sentiment: Sentiment | null) => {
  switch (sentiment) {
    case 'positive':
      return 'text-green-600 bg-green-100'
    case 'negative':
      return 'text-red-600 bg-red-100'
    default:
      return 'text-gray-600 bg-gray-100'
  }
}

const formatDuration = (seconds: number | null) => {
  if (!seconds) return '-'
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

const getTypeLabel = (type: ApiConversationType) => {
  switch (type) {
    case 'email':
      return 'Email'
    case 'call':
    case 'outbound_call':
      return 'Outbound Call'
    case 'inbound_call':
      return 'Inbound Call'
    case 'advisor':
      return 'Advisor'
    default:
      return type
  }
}

export function Conversations() {
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState('conversations')
  const [deepLinkHandled, setDeepLinkHandled] = useState(false)

  // Conversations state
  const [filters, setFilters] = useState<ConversationFilters>({
    page: 1,
    page_size: 25,
  })
  const [typeFilter, setTypeFilter] = useState<string>('calls')  // Default to showing all calls
  const [sentimentFilter, setSentimentFilter] = useState<string>('all')
  const [answeredByFilter, setAnsweredByFilter] = useState<string>('all')
  const [tagFilter, setTagFilter] = useState<string[]>([])
  const [isTagDropdownOpen, setIsTagDropdownOpen] = useState(false)
  const tagDropdownRef = useRef<HTMLDivElement>(null)
  const [searchInput, setSearchInput] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [selectedConversation, setSelectedConversation] = useState<ApiConversation | null>(null)

  // View mode and sorting state
  const [viewMode, setViewMode] = useState<'card' | 'table'>('table')  // Default to table view
  const [sortBy, setSortBy] = useState<'timestamp' | 'company' | 'type' | 'sentiment' | 'duration'>('timestamp')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  // Tag management state for details modal - hidden for now
  const [newTagInput, setNewTagInput] = useState('')
  const [_showTagSuggestions, setShowTagSuggestions] = useState(false)
  void _showTagSuggestions // Suppress unused warning - tags UI hidden

  // Email Queue state
  const [queueStatusFilter, setQueueStatusFilter] = useState<string>('')
  const [queueSearchQuery, setQueueSearchQuery] = useState('')
  const [queueSearchInput, setQueueSearchInput] = useState('')
  const [queuePage, setQueuePage] = useState(1)
  const [selectedQueueItem, setSelectedQueueItem] = useState<EmailQueueItem | null>(null)
  const [selectedQueueIds, setSelectedQueueIds] = useState<number[]>([])
  const [editForm, setEditForm] = useState({
    to_email: '',
    subject: '',
    contact_name: '',
    company_name: '',
  })

  // Share dialog state
  const [showShareDialog, setShowShareDialog] = useState(false)
  const [shareEmail, setShareEmail] = useState('')
  const [shareMessage, setShareMessage] = useState('')

  // Close tag dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (tagDropdownRef.current && !tagDropdownRef.current.contains(event.target as Node)) {
        setIsTagDropdownOpen(false)
      }
    }
    if (isTagDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isTagDropdownOpen])

  // Debounced conversation search - 800ms delay for better UX
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput)
      setFilters(prev => ({ ...prev, page: 1 }))
    }, 800)
    return () => clearTimeout(timer)
  }, [searchInput])

  // Debounced queue search
  useEffect(() => {
    const timer = setTimeout(() => {
      setQueueSearchQuery(queueSearchInput)
      setQueuePage(1)
    }, 500)
    return () => clearTimeout(timer)
  }, [queueSearchInput])

  // Build filters for API
  const apiFilters = useMemo(() => {
    const f: ConversationFilters = {
      page: filters.page,
      page_size: filters.page_size,
      sort_by: sortBy,
      sort_order: sortOrder,
    }
    if (typeFilter !== 'all') f.type = typeFilter as ConversationType
    if (sentimentFilter !== 'all') f.sentiment = sentimentFilter as Sentiment
    if (answeredByFilter !== 'all') f.answered_by = answeredByFilter
    if (searchQuery) f.search = searchQuery
    if (dateFrom) f.date_from = dateFrom
    if (dateTo) f.date_to = dateTo
    return f
  }, [filters, typeFilter, sentimentFilter, answeredByFilter, searchQuery, dateFrom, dateTo, sortBy, sortOrder])

  // Fetch conversations
  const { data, isLoading, error } = useQuery({
    queryKey: ['conversations', apiFilters],
    queryFn: () => conversationsApi.getAll(apiFilters),
    placeholderData: (previousData) => previousData, // Keep showing previous data while loading new results
  })

  // Fetch email queue
  const { data: queueData, isLoading: queueLoading, refetch: refetchQueue } = useQuery({
    queryKey: ['email-queue', queueStatusFilter, queueSearchQuery, queuePage],
    queryFn: () => emailQueueApi.getAll({
      status: queueStatusFilter || undefined,
      search: queueSearchQuery || undefined,
      limit: 20,
      offset: (queuePage - 1) * 20,
    }),
    enabled: activeTab === 'email-queue',
  })

  // Fetch email queue stats
  const { data: queueStats, refetch: refetchStats } = useQuery({
    queryKey: ['email-queue-stats'],
    queryFn: () => emailQueueApi.getStats(),
    enabled: activeTab === 'email-queue',
  })

  // Fetch detailed item for review modal
  const [reviewItemId, setReviewItemId] = useState<number | null>(null)
  const { data: reviewItemData } = useQuery({
    queryKey: ['email-queue-item', reviewItemId],
    queryFn: () => reviewItemId ? emailQueueApi.getById(reviewItemId) : null,
    enabled: !!reviewItemId,
  })
  const reviewItem = reviewItemData?.data

  // Review form state
  const [reviewNotes, setReviewNotes] = useState('')

  // Email queue mutations
  const approveMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data?: { review_notes?: string; to_email?: string; subject?: string; contact_name?: string; company_name?: string } }) =>
      emailQueueApi.approve(id, data),
    onSuccess: () => {
      refetchQueue()
      refetchStats()
      setReviewItemId(null)
      setSelectedQueueItem(null)
      setReviewNotes('')
    },
  })

  const rejectMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data?: { review_notes?: string } }) =>
      emailQueueApi.reject(id, data),
    onSuccess: () => {
      refetchQueue()
      refetchStats()
      setReviewItemId(null)
      setSelectedQueueItem(null)
      setReviewNotes('')
    },
  })

  const cancelMutation = useMutation({
    mutationFn: (id: number) => emailQueueApi.cancel(id),
    onSuccess: () => {
      refetchQueue()
      refetchStats()
      setSelectedQueueItem(null)
    },
  })

  const retryMutation = useMutation({
    mutationFn: (id: number) => emailQueueApi.retry(id),
    onSuccess: () => {
      refetchQueue()
      refetchStats()
      setSelectedQueueItem(null)
    },
  })

  const resendMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data?: { to_email?: string; subject?: string } }) =>
      emailQueueApi.resend(id, data),
    onSuccess: () => {
      refetchQueue()
      refetchStats()
      setSelectedQueueItem(null)
    },
  })

  const batchDeleteMutation = useMutation({
    mutationFn: (ids: number[]) => emailQueueApi.batchDelete(ids),
    onSuccess: () => {
      refetchQueue()
      refetchStats()
      setSelectedQueueIds([])
    },
  })

  const refreshTemplateMutation = useMutation({
    mutationFn: (id: number) => emailQueueApi.refreshTemplate(id),
    onSuccess: (response) => {
      refetchQueue()
      // Refetch the review item to get updated content
      if (reviewItemId) {
        queryClient.invalidateQueries({ queryKey: ['email-queue-item', reviewItemId] })
      }
      // Show success notification (you could add a toast here)
      console.log('Template refreshed:', response.data?.template_name)
    },
  })

  const aiRefineMutation = useMutation({
    mutationFn: ({ id, instructions }: { id: number; instructions?: string }) =>
      emailQueueApi.aiRefine(id, instructions),
    onSuccess: (response) => {
      refetchQueue()
      // Refetch the review item to get updated content
      if (reviewItemId) {
        queryClient.invalidateQueries({ queryKey: ['email-queue-item', reviewItemId] })
      }
      // Show success notification
      console.log('AI refinement applied:', response.data?.changes_made)
    },
  })

  // Fetch available tags for filtering
  const { data: tagsData } = useQuery({
    queryKey: ['conversation-tags'],
    queryFn: () => conversationsApi.getTags(),
  })
  const availableTags = tagsData?.data?.tags || []

  // Tag add mutation - handles both outbound and inbound calls
  const addTagMutation = useMutation({
    mutationFn: ({ callId, tag, isInbound }: { callId: string | number; tag: string; isInbound: boolean }) =>
      isInbound ? conversationsApi.addInboundTag(callId, tag) : conversationsApi.addTag(callId, tag),
    onSuccess: (response) => {
      // Update selected conversation with new tags
      if (selectedConversation) {
        setSelectedConversation({
          ...selectedConversation,
          tags: response.data?.tags || [],
        })
      }
      // Refresh conversations and tags list
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
      queryClient.invalidateQueries({ queryKey: ['conversation-tags'] })
      setNewTagInput('')
      setShowTagSuggestions(false)
    },
  })

  // Tag remove mutation - handles both outbound and inbound calls
  const removeTagMutation = useMutation({
    mutationFn: ({ callId, tag, isInbound }: { callId: string | number; tag: string; isInbound: boolean }) =>
      isInbound ? conversationsApi.removeInboundTag(callId, tag) : conversationsApi.removeTag(callId, tag),
    onSuccess: (response) => {
      // Update selected conversation with new tags
      if (selectedConversation) {
        setSelectedConversation({
          ...selectedConversation,
          tags: response.data?.tags || [],
        })
      }
      // Refresh conversations and tags list
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
      queryClient.invalidateQueries({ queryKey: ['conversation-tags'] })
    },
  })

  // Share conversation mutation
  const shareMutation = useMutation({
    mutationFn: (data: { recipient_email: string; conversation_type: string; conversation_id: string; message?: string }) =>
      conversationsApi.share(data),
    onSuccess: () => {
      setShowShareDialog(false)
      setShareEmail('')
      setShareMessage('')
    },
  })

  // Handle share button click
  const handleShareClick = () => {
    setShowShareDialog(true)
  }

  // Handle share submit
  const handleShareSubmit = () => {
    if (!selectedConversation || !shareEmail) return
    shareMutation.mutate({
      recipient_email: shareEmail,
      conversation_type: selectedConversation.type,
      conversation_id: String(selectedConversation.id),
      message: shareMessage || undefined,
    })
  }

  // Cast to ApiConversation since backend returns different field names than frontend Conversation type
  const conversations = (data?.data?.items || []) as unknown as ApiConversation[]
  const totalPages = data?.data?.total_pages || 1
  const totalItems = data?.data?.total || 0
  // Get stats from API response (total counts across all pages, not just current page)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const stats = (data?.data as any)?.stats as { email: number; call: number; inbound_call: number; advisor: number; total: number } | undefined

  const queueItems = queueData?.data?.items || []
  const queueTotal = queueData?.data?.total || 0
  const queueTotalPages = Math.ceil(queueTotal / 20)

  // Handle deep link from URL parameters (e.g., /conversations?type=inbound_call&id=96)
  useEffect(() => {
    if (deepLinkHandled) return

    const urlType = searchParams.get('type')
    const urlId = searchParams.get('id')

    if (!urlType || !urlId) return

    // Fetch the conversation directly by type and ID (don't rely on current list)
    const fetchConversation = async () => {
      try {
        console.log(`[DeepLink] Fetching ${urlType} with id ${urlId}`)
        if (urlType === 'inbound_call') {
          const response = await conversationsApi.getInboundCall(urlId)
          console.log('[DeepLink] Inbound response:', response.data)
          if (response.data?.inbound_call) {
            setSelectedConversation(response.data.inbound_call as unknown as ApiConversation)
            setDeepLinkHandled(true)
          }
        } else if (urlType === 'call' || urlType === 'outbound_call') {
          const response = await conversationsApi.getCallSummary(urlId)
          console.log('[DeepLink] Call summary response:', response.data)
          if (response.data?.call_summary) {
            // Map call_summary fields to ApiConversation format
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const summary = response.data.call_summary as any
            setSelectedConversation({
              id: summary.id,
              type: 'call',
              timestamp: summary.created_at,
              summary: summary.summary,
              transcript: summary.transcript,
              sentiment: summary.sentiment_label,
              duration: summary.duration_seconds,
              answered_by: summary.answered_by,
              recording_url: summary.recording_url,
              tags: summary.tags || [],
            } as ApiConversation)
            setDeepLinkHandled(true)
          }
        }
        // Clear URL params after fetching
        setSearchParams({}, { replace: true })
      } catch (err) {
        console.error('[DeepLink] Failed to fetch conversation:', err)
        setDeepLinkHandled(true)
        // Clear URL params even on error
        setSearchParams({}, { replace: true })
      }
    }

    fetchConversation()
  }, [deepLinkHandled, searchParams, setSearchParams])

  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage }))
  }

  const handlePageSizeChange = (size: string) => {
    setFilters(prev => ({ ...prev, page_size: parseInt(size), page: 1 }))
  }

  const clearFilters = () => {
    setTypeFilter('calls')  // Reset to default (calls)
    setSentimentFilter('all')
    setAnsweredByFilter('all')
    setTagFilter([])
    setSearchInput('')
    setSearchQuery('')
    setDateFrom('')
    setDateTo('')
    setFilters({ page: 1, page_size: 25 })
  }

  // Check if any non-default filters are active
  const hasActiveFilters = typeFilter !== 'calls' || sentimentFilter !== 'all' || answeredByFilter !== 'all' || tagFilter.length > 0 || searchInput || dateFrom || dateTo

  // Toggle tag in filter
  const toggleTagInFilter = (tag: string) => {
    setTagFilter(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    )
    setFilters(prev => ({ ...prev, page: 1 }))
  }

  // Filter conversations by tags (client-side for multiple tags)
  // Sorting is handled server-side via API
  const filteredConversations = useMemo(() => {
    if (tagFilter.length === 0) return conversations
    return conversations.filter(conv => {
      const convTags = conv.tags || []
      // AND logic: conversation must have ALL selected tags
      return tagFilter.every(tag => convTags.includes(tag))
    })
  }, [conversations, tagFilter])

  // Toggle sort order or change sort field - resets to page 1
  const handleSort = (field: typeof sortBy) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(field)
      setSortOrder('desc')
    }
    // Reset to page 1 when sorting changes
    setFilters(prev => ({ ...prev, page: 1 }))
  }

  // Get sort icon for a field
  const getSortIcon = (field: typeof sortBy) => {
    if (sortBy !== field) return <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
    return sortOrder === 'asc'
      ? <ArrowUp className="h-4 w-4 text-primary" />
      : <ArrowDown className="h-4 w-4 text-primary" />
  }

  // Handle adding tag to conversation - hidden for now
  const _handleAddTag = () => {
    if (!selectedConversation || !newTagInput.trim()) return
    const tag = newTagInput.trim().toLowerCase()
    // Only calls have tags (call or outbound_call or inbound_call)
    const isInbound = selectedConversation.type === 'inbound_call'
    if (selectedConversation.type === 'call' || selectedConversation.type === 'outbound_call' || isInbound) {
      addTagMutation.mutate({ callId: selectedConversation.id, tag, isInbound })
    }
  }
  void _handleAddTag // Suppress unused warning - tags UI hidden

  // Handle removing tag from conversation - hidden for now
  const _handleRemoveTag = (tag: string) => {
    if (!selectedConversation) return
    const isInbound = selectedConversation.type === 'inbound_call'
    if (selectedConversation.type === 'call' || selectedConversation.type === 'outbound_call' || isInbound) {
      removeTagMutation.mutate({ callId: selectedConversation.id, tag, isInbound })
    }
  }
  void _handleRemoveTag // Suppress unused warning - tags UI hidden

  // Filtered tag suggestions based on input - hidden for now
  const _tagSuggestions = useMemo(() => {
    // Only show suggestions when user has typed something
    if (!newTagInput.trim()) return []
    const input = newTagInput.trim().toLowerCase()
    return availableTags.filter(tag =>
      tag.toLowerCase().includes(input) &&
      !(selectedConversation?.tags || []).includes(tag)
    ).slice(0, 10)
  }, [newTagInput, availableTags, selectedConversation?.tags])
  void _tagSuggestions // Suppress unused warning - tags UI hidden

  const openQueueItemDialog = (item: EmailQueueItem) => {
    setSelectedQueueItem(item)
    setEditForm({
      to_email: item.to_email,
      subject: item.subject || '',
      contact_name: (item.template_data?.contact_name as string) || '',
      company_name: (item.template_data?.company_name as string) || item.lead_company || '',
    })
    // Also fetch full context for review items
    if (item.status === 'pending_review') {
      setReviewItemId(item.id)
    }
  }

  const openReviewModal = (item: EmailQueueItem) => {
    setSelectedQueueItem(item)
    setReviewItemId(item.id)
    setEditForm({
      to_email: item.to_email,
      subject: item.subject || '',
      contact_name: (item.template_data?.contact_name as string) || '',
      company_name: (item.template_data?.company_name as string) || item.lead_company || '',
    })
    setReviewNotes('')
  }

  const handleApprove = () => {
    if (!selectedQueueItem) return
    approveMutation.mutate({
      id: selectedQueueItem.id,
      data: {
        review_notes: reviewNotes || undefined,
        to_email: editForm.to_email,
        subject: editForm.subject || undefined,
        contact_name: editForm.contact_name || undefined,
        company_name: editForm.company_name || undefined,
      },
    })
  }

  const handleReject = () => {
    if (!selectedQueueItem) return
    rejectMutation.mutate({
      id: selectedQueueItem.id,
      data: { review_notes: reviewNotes || undefined },
    })
  }

  const handleCancel = () => {
    if (!selectedQueueItem) return
    cancelMutation.mutate(selectedQueueItem.id)
  }

  const handleRetry = () => {
    if (!selectedQueueItem) return
    retryMutation.mutate(selectedQueueItem.id)
  }

  const handleResend = () => {
    if (!selectedQueueItem) return
    resendMutation.mutate({
      id: selectedQueueItem.id,
      data: {
        to_email: editForm.to_email !== selectedQueueItem.to_email ? editForm.to_email : undefined,
        subject: editForm.subject !== selectedQueueItem.subject ? editForm.subject : undefined,
      },
    })
  }

  const closeModals = () => {
    setSelectedQueueItem(null)
    setReviewItemId(null)
    setReviewNotes('')
  }

  const getQueueStatusBadge = (status: string, sentAt?: string) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-500">Queued</Badge>
      case 'pending_review':
        return <Badge className="bg-orange-500">Needs Review</Badge>
      case 'processing':
        // If there's a sent_at timestamp, it was actually sent but status wasn't updated
        return sentAt
          ? <Badge className="bg-green-500">Delivered</Badge>
          : <Badge className="bg-blue-500">In Progress</Badge>
      case 'sent':
        return <Badge className="bg-green-500">Delivered</Badge>
      case 'failed':
        return <Badge className="bg-red-500">Failed</Badge>
      case 'cancelled':
        return <Badge className="bg-gray-500">Cancelled</Badge>
      case 'rejected':
        return <Badge className="bg-red-700">Rejected</Badge>
      default:
        return <Badge>{status}</Badge>
    }
  }

  if (error) {
    return (
      <div className="p-6">
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">Failed to load conversations. Please try again.</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">AI Agent Conversations</h1>
          <p className="text-muted-foreground">
            View email threads, call transcripts & sentiment analysis
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="conversations">Conversations</TabsTrigger>
          <TabsTrigger value="email-queue" className="flex items-center gap-2">
            Email Queue
            {queueStats?.data?.pending_review ? (
              <Badge className="bg-orange-500 text-white text-xs px-1.5 py-0.5">
                {queueStats.data.pending_review}
              </Badge>
            ) : null}
          </TabsTrigger>
        </TabsList>

        {/* Conversations Tab */}
        <TabsContent value="conversations" className="space-y-6">
          {/* Filters */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-6">
            {/* Search */}
            <div className="relative lg:col-span-2">
              {isLoading && searchInput ? (
                <Loader2 className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-primary animate-spin" />
              ) : (
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              )}
              <Input
                placeholder="Search conversations..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Type Filter */}
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                {CONVERSATION_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Sentiment Filter */}
            <Select value={sentimentFilter} onValueChange={setSentimentFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Sentiment" />
              </SelectTrigger>
              <SelectContent>
                {SENTIMENTS.map(s => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Answered By Filter */}
            <Select value={answeredByFilter} onValueChange={setAnsweredByFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Answered By" />
              </SelectTrigger>
              <SelectContent>
                {ANSWERED_BY.map(a => (
                  <SelectItem key={a.value} value={a.value}>
                    {a.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Tags Filter */}
            <div className="relative" ref={tagDropdownRef}>
              <div
                className="flex items-center justify-between px-3 py-2 border rounded-md cursor-pointer bg-background hover:bg-muted/50"
                onClick={() => setIsTagDropdownOpen(!isTagDropdownOpen)}
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Tag className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="text-sm truncate">
                    {tagFilter.length === 0 ? 'All Tags' : `${tagFilter.length} tag${tagFilter.length > 1 ? 's' : ''}`}
                  </span>
                </div>
                <ChevronRight className={cn('h-4 w-4 text-muted-foreground transition-transform', isTagDropdownOpen && 'rotate-90')} />
              </div>
              {isTagDropdownOpen && (
                <div className="absolute z-50 mt-1 w-full bg-background border rounded-md shadow-lg max-h-64 overflow-y-auto">
                  {tagFilter.length > 0 && (
                    <div
                      className="px-3 py-2 text-sm text-muted-foreground hover:bg-muted cursor-pointer border-b"
                      onClick={() => { setTagFilter([]); setIsTagDropdownOpen(false) }}
                    >
                      Clear filter
                    </div>
                  )}
                  {availableTags.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-muted-foreground">No tags available</div>
                  ) : (
                    availableTags.map(tag => (
                      <label
                        key={tag}
                        className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={tagFilter.includes(tag)}
                          onChange={() => toggleTagInFilter(tag)}
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span>{tag}</span>
                      </label>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Date From */}
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <Input
                type="date"
                placeholder="From"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Date To */}
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <Input
                type="date"
                placeholder="To"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {hasActiveFilters && (
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <span className="text-sm text-muted-foreground">Active filters:</span>
              {typeFilter !== 'all' && (
                <Badge variant="secondary">{CONVERSATION_TYPES.find(t => t.value === typeFilter)?.label}</Badge>
              )}
              {sentimentFilter !== 'all' && (
                <Badge variant="secondary">{SENTIMENTS.find(s => s.value === sentimentFilter)?.label}</Badge>
              )}
              {answeredByFilter !== 'all' && (
                <Badge variant="secondary">Answered: {ANSWERED_BY.find(a => a.value === answeredByFilter)?.label}</Badge>
              )}
              {tagFilter.length > 0 && tagFilter.map(tag => (
                <Badge
                  key={tag}
                  variant="secondary"
                  className="bg-indigo-100 text-indigo-800 cursor-pointer hover:bg-indigo-200"
                  onClick={() => toggleTagInFilter(tag)}
                >
                  {tag} <X className="h-3 w-3 ml-1" />
                </Badge>
              ))}
              {searchInput && <Badge variant="secondary">Search: {searchInput}</Badge>}
              {dateFrom && <Badge variant="secondary">From: {dateFrom}</Badge>}
              {dateTo && <Badge variant="secondary">To: {dateTo}</Badge>}
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Clear all
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats Summary - Uses total counts from API, not just current page */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Mail className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {stats?.email ?? conversations.filter(c => c.type === 'email').length}
                </p>
                <p className="text-sm text-muted-foreground">Emails</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <PhoneOutgoing className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {stats?.call ?? conversations.filter(c => c.type === 'call' || c.type === 'outbound_call').length}
                </p>
                <p className="text-sm text-muted-foreground">Outbound Calls</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-teal-100 rounded-lg">
                <PhoneIncoming className="h-5 w-5 text-teal-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {stats?.inbound_call ?? conversations.filter(c => c.type === 'inbound_call').length}
                </p>
                <p className="text-sm text-muted-foreground">Inbound Calls</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <MessageSquare className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {stats?.advisor ?? conversations.filter(c => c.type === 'advisor').length}
                </p>
                <p className="text-sm text-muted-foreground">Advisor</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Conversations List */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <CardTitle className="text-base flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              All Conversations
              <Badge variant="outline" className="ml-2">{totalItems} total</Badge>
            </CardTitle>
            <div className="flex items-center gap-4 flex-wrap">
              {/* Sort Controls */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Sort:</span>
                <Select value={sortBy} onValueChange={(v) => setSortBy(v as typeof sortBy)}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="timestamp">Date</SelectItem>
                    <SelectItem value="company">Company</SelectItem>
                    <SelectItem value="type">Type</SelectItem>
                    <SelectItem value="sentiment">Sentiment</SelectItem>
                    <SelectItem value="duration">Duration</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                  title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
                >
                  {sortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
                </Button>
              </div>

              {/* View Mode Toggle */}
              <div className="flex items-center border rounded-md">
                <Button
                  variant={viewMode === 'card' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('card')}
                  className="rounded-r-none"
                  title="Card View"
                >
                  <LayoutGrid className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === 'table' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('table')}
                  className="rounded-l-none"
                  title="Table View"
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>

              {/* Page Size */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Show:</span>
                <Select
                  value={String(filters.page_size)}
                  onValueChange={handlePageSizeChange}
                >
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZES.map(size => (
                      <SelectItem key={size} value={String(size)}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="flex gap-4 animate-pulse p-4 border rounded-lg">
                  <div className="w-12 h-12 bg-muted rounded-lg" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-muted rounded w-3/4" />
                    <div className="h-3 bg-muted rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredConversations.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-medium text-lg">No conversations found</h3>
              <p className="text-muted-foreground">
                {hasActiveFilters
                  ? 'Try adjusting your filters'
                  : 'Conversations will appear here as you interact with leads'}
              </p>
            </div>
          ) : viewMode === 'table' ? (
            /* Table View */
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th
                      className="text-left p-3 font-medium cursor-pointer hover:bg-muted"
                      onClick={() => handleSort('timestamp')}
                    >
                      <div className="flex items-center gap-1">
                        Date/Time
                        {getSortIcon('timestamp')}
                      </div>
                    </th>
                    <th
                      className="text-left p-3 font-medium cursor-pointer hover:bg-muted"
                      onClick={() => handleSort('type')}
                    >
                      <div className="flex items-center gap-1">
                        Type
                        {getSortIcon('type')}
                      </div>
                    </th>
                    <th
                      className="text-left p-3 font-medium cursor-pointer hover:bg-muted"
                      onClick={() => handleSort('company')}
                    >
                      <div className="flex items-center gap-1">
                        Company
                        {getSortIcon('company')}
                      </div>
                    </th>
                    <th className="text-left p-3 font-medium">Subject / Phone</th>
                    <th
                      className="text-left p-3 font-medium cursor-pointer hover:bg-muted"
                      onClick={() => handleSort('sentiment')}
                    >
                      <div className="flex items-center gap-1">
                        Sentiment
                        {getSortIcon('sentiment')}
                      </div>
                    </th>
                    <th
                      className="text-left p-3 font-medium cursor-pointer hover:bg-muted"
                      onClick={() => handleSort('duration')}
                    >
                      <div className="flex items-center gap-1">
                        Duration
                        {getSortIcon('duration')}
                      </div>
                    </th>
                    <th className="text-left p-3 font-medium">Answered</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredConversations.map(conversation => {
                    const SentimentIcon = getSentimentIcon(conversation.sentiment)
                    const sentimentColor = getSentimentColor(conversation.sentiment)

                    return (
                      <tr
                        key={conversation.id}
                        className="border-b hover:bg-muted/50 transition-colors cursor-pointer"
                        onClick={() => {
                          setNewTagInput('')
                          setShowTagSuggestions(false)
                          setSelectedConversation(conversation)
                        }}
                      >
                        <td className="p-3 whitespace-nowrap">
                          <div className="text-sm">{formatDate(conversation.timestamp || conversation.created_at || '')}</div>
                          <div className="text-xs text-muted-foreground">{formatTime(conversation.timestamp || conversation.created_at || '')}</div>
                        </td>
                        <td className="p-3">
                          <Badge className={cn('text-white text-xs', getTypeBadgeColor(conversation.type))}>
                            {getTypeLabel(conversation.type)}
                          </Badge>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-1">
                            <Building2 className="h-3 w-3 text-muted-foreground" />
                            <span className="truncate max-w-[200px]">
                              {conversation.lead?.company_name || conversation.lead_company || '-'}
                            </span>
                          </div>
                        </td>
                        <td className="p-3">
                          <span className="truncate max-w-[250px] block">
                            {conversation.type === 'call' || conversation.type === 'outbound_call'
                              ? conversation.lead_phone || 'No phone'
                              : conversation.type === 'inbound_call'
                              ? conversation.caller_phone || 'Unknown'
                              : conversation.subject || conversation.intent || 'No subject'}
                          </span>
                        </td>
                        <td className="p-3">
                          {conversation.sentiment ? (
                            <div className={cn('flex items-center gap-1 px-2 py-0.5 rounded text-xs w-fit', sentimentColor)}>
                              <SentimentIcon className="h-3 w-3" />
                              <span className="capitalize">{conversation.sentiment}</span>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="p-3">
                          {conversation.duration ? (
                            <span className="text-sm">{formatDuration(conversation.duration)}</span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="p-3">
                          {(conversation.type === 'call' || conversation.type === 'outbound_call') && conversation.answered_by ? (
                            conversation.answered_by === 'human' ? (
                              <CallPreviewHoverCard
                                summary={conversation.summary}
                                transcript={conversation.transcript}
                              >
                                <Badge
                                  variant="secondary"
                                  className="text-xs bg-green-100 text-green-800 cursor-help"
                                >
                                  {conversation.answered_by.charAt(0).toUpperCase() + conversation.answered_by.slice(1)}
                                </Badge>
                              </CallPreviewHoverCard>
                            ) : (
                              <Badge
                                variant="secondary"
                                className={cn(
                                  'text-xs',
                                  conversation.answered_by === 'voicemail' && 'bg-yellow-100 text-yellow-800',
                                  conversation.answered_by === 'ivr' && 'bg-blue-100 text-blue-800',
                                  !['human', 'voicemail', 'ivr'].includes(conversation.answered_by) && 'bg-gray-100 text-gray-800'
                                )}
                              >
                                {conversation.answered_by.charAt(0).toUpperCase() + conversation.answered_by.slice(1)}
                              </Badge>
                            )
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            /* Card View */
            <div className="space-y-3">
              {filteredConversations.map(conversation => {
                const Icon = getTypeIcon(conversation.type)
                const colorClass = getTypeColor(conversation.type)
                const SentimentIcon = getSentimentIcon(conversation.sentiment)
                const sentimentColor = getSentimentColor(conversation.sentiment)

                return (
                  <div
                    key={conversation.id}
                    className="flex gap-4 p-4 border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                    onClick={() => {
                      setNewTagInput('')
                      setShowTagSuggestions(false)
                      setSelectedConversation(conversation)
                    }}
                  >
                    {/* Icon */}
                    <div
                      className={cn(
                        'flex items-center justify-center w-12 h-12 rounded-lg border',
                        colorClass
                      )}
                    >
                      <Icon className="h-5 w-5" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          {/* Type and Date */}
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <Badge className={cn('text-white text-xs', getTypeBadgeColor(conversation.type))}>
                              {getTypeLabel(conversation.type)}
                            </Badge>
                            {conversation.sentiment && (
                              <div className={cn('flex items-center gap-1 px-2 py-0.5 rounded text-xs', sentimentColor)}>
                                <SentimentIcon className="h-3 w-3" />
                                <span className="capitalize">{conversation.sentiment}</span>
                              </div>
                            )}
                            {/* Answered By badge for outbound calls - with hover preview for human-answered */}
                            {(conversation.type === 'call' || conversation.type === 'outbound_call') && conversation.answered_by && (
                              conversation.answered_by === 'human' ? (
                                <CallPreviewHoverCard
                                  summary={conversation.summary}
                                  transcript={conversation.transcript}
                                >
                                  <Badge
                                    variant="secondary"
                                    className="text-xs bg-green-100 text-green-800 cursor-help"
                                  >
                                    {conversation.answered_by.charAt(0).toUpperCase() + conversation.answered_by.slice(1)}
                                  </Badge>
                                </CallPreviewHoverCard>
                              ) : (
                                <Badge
                                  variant="secondary"
                                  className={cn(
                                    'text-xs',
                                    conversation.answered_by === 'voicemail' && 'bg-yellow-100 text-yellow-800',
                                    conversation.answered_by === 'ivr' && 'bg-blue-100 text-blue-800',
                                    !['human', 'voicemail', 'ivr'].includes(conversation.answered_by) && 'bg-gray-100 text-gray-800'
                                  )}
                                >
                                  {conversation.answered_by.charAt(0).toUpperCase() + conversation.answered_by.slice(1)}
                                </Badge>
                              )
                            )}
                            {conversation.duration && (
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {formatDuration(conversation.duration)}
                              </span>
                            )}
                          </div>

                          {/* Subject/Title with phone number */}
                          <p className="font-medium truncate">
                            {conversation.type === 'call' || conversation.type === 'outbound_call'
                              ? `Outbound Call - ${conversation.lead_phone || 'No phone'}`
                              : conversation.type === 'inbound_call'
                              ? `Inbound Call - ${conversation.caller_phone || 'Unknown'}`
                              : conversation.subject || conversation.intent || conversation.intent_detected || 'No subject'}
                          </p>

                          {/* Content Preview - API returns 'summary' not 'content' */}
                          {conversation.summary && (
                            <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                              {conversation.summary}
                            </p>
                          )}

                          {/* Tags display */}
                          {conversation.tags && conversation.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {conversation.tags.map((tag, idx) => (
                                <Badge
                                  key={idx}
                                  variant="secondary"
                                  className="bg-indigo-100 text-indigo-800 text-xs px-2 py-0.5"
                                >
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          )}

                          {/* Lead Info */}
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground flex-wrap">
                            {(conversation.lead || conversation.lead_company) && (
                              <span className="flex items-center gap-1">
                                <Building2 className="h-3 w-3" />
                                {conversation.lead?.company_name || conversation.lead_company}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Timestamp - API returns 'timestamp' not 'created_at' */}
                        <div className="text-right text-xs text-muted-foreground whitespace-nowrap">
                          <p>{formatDate(conversation.timestamp || conversation.created_at || '')}</p>
                          <p>{formatTime(conversation.timestamp || conversation.created_at || '')}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t">
              <p className="text-sm text-muted-foreground">
                Page {filters.page} of {totalPages}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(filters.page! - 1)}
                  disabled={filters.page === 1}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(filters.page! + 1)}
                  disabled={filters.page === totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        {/* Email Queue Tab */}
        <TabsContent value="email-queue" className="space-y-6">
          {/* Queue Stats */}
          <div className="grid grid-cols-2 md:grid-cols-7 gap-4">
            <Card
              className={cn(
                "cursor-pointer hover:shadow-md transition border-2",
                queueStatusFilter === 'pending_review' ? 'border-orange-500' : 'border-transparent'
              )}
              onClick={() => setQueueStatusFilter('pending_review')}
            >
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-gray-600">Needs Review</p>
                <p className="text-2xl font-bold text-orange-600">
                  {queueStats?.data?.pending_review ?? '-'}
                </p>
              </CardContent>
            </Card>
            <Card
              className={cn(
                "cursor-pointer hover:shadow-md transition border-2",
                queueStatusFilter === 'pending' ? 'border-yellow-500' : 'border-transparent'
              )}
              onClick={() => setQueueStatusFilter('pending')}
            >
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-gray-600">Queued</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {queueStats?.data?.pending ?? '-'}
                </p>
              </CardContent>
            </Card>
            <Card
              className={cn(
                "cursor-pointer hover:shadow-md transition border-2",
                queueStatusFilter === 'processing' ? 'border-blue-500' : 'border-transparent'
              )}
              onClick={() => setQueueStatusFilter('processing')}
            >
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-gray-600">Sending</p>
                <p className="text-2xl font-bold text-blue-600">
                  {queueStats?.data?.processing ?? '-'}
                </p>
              </CardContent>
            </Card>
            <Card
              className={cn(
                "cursor-pointer hover:shadow-md transition border-2",
                queueStatusFilter === 'sent' ? 'border-green-500' : 'border-transparent'
              )}
              onClick={() => setQueueStatusFilter('sent')}
            >
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-gray-600">Delivered</p>
                <p className="text-2xl font-bold text-green-600">
                  {queueStats?.data?.sent ?? '-'}
                </p>
              </CardContent>
            </Card>
            <Card
              className={cn(
                "cursor-pointer hover:shadow-md transition border-2",
                queueStatusFilter === 'failed' ? 'border-red-500' : 'border-transparent'
              )}
              onClick={() => setQueueStatusFilter('failed')}
            >
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-gray-600">Failed</p>
                <p className="text-2xl font-bold text-red-600">
                  {queueStats?.data?.failed ?? '-'}
                </p>
              </CardContent>
            </Card>
            <Card
              className={cn(
                "cursor-pointer hover:shadow-md transition border-2",
                queueStatusFilter === 'rejected' ? 'border-red-700' : 'border-transparent'
              )}
              onClick={() => setQueueStatusFilter('rejected')}
            >
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-gray-600">Rejected</p>
                <p className="text-2xl font-bold text-red-700">
                  {queueStats?.data?.rejected ?? '-'}
                </p>
              </CardContent>
            </Card>
            <Card
              className={cn(
                "cursor-pointer hover:shadow-md transition border-2",
                queueStatusFilter === 'cancelled' ? 'border-gray-500' : 'border-transparent'
              )}
              onClick={() => setQueueStatusFilter('cancelled')}
            >
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-gray-600">Cancelled</p>
                <p className="text-2xl font-bold text-gray-600">
                  {queueStats?.data?.cancelled ?? '-'}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Queue Filters & List */}
          <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div>
                  <Label className="mb-2 block">Status</Label>
                  <Select value={queueStatusFilter || 'all'} onValueChange={(v) => setQueueStatusFilter(v === 'all' ? '' : v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="pending_review">Needs Review</SelectItem>
                      <SelectItem value="pending">Queued</SelectItem>
                      <SelectItem value="processing">Sending</SelectItem>
                      <SelectItem value="sent">Delivered</SelectItem>
                      <SelectItem value="failed">Failed</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label className="mb-2 block">Search</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder="Search by email, company, subject..."
                      value={queueSearchInput}
                      onChange={(e) => setQueueSearchInput(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                </div>
                <div className="flex items-end gap-2">
                  <Button
                    onClick={() => { refetchQueue(); refetchStats(); }}
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Refresh
                  </Button>
                  {selectedQueueIds.length > 0 && (
                    <Button
                      variant="destructive"
                      onClick={() => {
                        if (confirm(`Delete ${selectedQueueIds.length} selected item(s)?`)) {
                          batchDeleteMutation.mutate(selectedQueueIds)
                        }
                      }}
                      disabled={batchDeleteMutation.isPending}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete ({selectedQueueIds.length})
                    </Button>
                  )}
                </div>
              </div>

              {/* Queue Items Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 w-10">
                        <Checkbox
                          checked={queueItems.length > 0 && selectedQueueIds.length === queueItems.length}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedQueueIds(queueItems.map(item => item.id))
                            } else {
                              setSelectedQueueIds([])
                            }
                          }}
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Recipient</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {queueLoading ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                          <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                          Loading email queue...
                        </td>
                      </tr>
                    ) : queueItems.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                          <Mail className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          No emails in queue
                        </td>
                      </tr>
                    ) : (
                      queueItems.map((item) => (
                        <tr key={item.id} className={cn("hover:bg-gray-50", selectedQueueIds.includes(item.id) && "bg-blue-50")}>
                          <td className="px-4 py-3">
                            <Checkbox
                              checked={selectedQueueIds.includes(item.id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedQueueIds(prev => [...prev, item.id])
                                } else {
                                  setSelectedQueueIds(prev => prev.filter(id => id !== item.id))
                                }
                              }}
                            />
                          </td>
                          <td className="px-4 py-3">
                            {getQueueStatusBadge(item.status, item.sent_at)}
                          </td>
                          <td className="px-4 py-3">
                            <div>
                              <p className="text-sm font-medium">{item.to_email}</p>
                              {item.lead_company && (
                                <p className="text-xs text-gray-500">{item.lead_company}</p>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-sm truncate max-w-[200px]">
                              {item.subject || '-'}
                            </p>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                              {item.source}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {item.created_at ? formatDate(item.created_at) : '-'}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              {item.status === 'pending_review' ? (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                                    onClick={() => openReviewModal(item)}
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                    onClick={() => {
                                      setSelectedQueueItem(item)
                                      handleApprove()
                                    }}
                                    disabled={approveMutation.isPending}
                                  >
                                    <CheckCircle2 className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => openReviewModal(item)}
                                  >
                                    <XCircle className="h-4 w-4" />
                                  </Button>
                                </>
                              ) : (item.status === 'sent' || item.status === 'failed' || (item.status === 'processing' && item.sent_at)) ? (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openQueueItemDialog(item)}
                                    title="View details"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                    onClick={() => openQueueItemDialog(item)}
                                    title="Resend email"
                                  >
                                    <RefreshCw className="h-4 w-4" />
                                  </Button>
                                </>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openQueueItemDialog(item)}
                                >
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Queue Pagination */}
              {queueTotalPages > 1 && (
                <div className="flex items-center justify-between mt-6 pt-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    Page {queuePage} of {queueTotalPages}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setQueuePage(p => p - 1)}
                      disabled={queuePage === 1}
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setQueuePage(p => p + 1)}
                      disabled={queuePage >= queueTotalPages}
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Conversation Detail Dialog */}
      <Dialog open={!!selectedConversation} onOpenChange={() => {
        setNewTagInput('')
        setShowTagSuggestions(false)
        setSelectedConversation(null)
      }}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          {selectedConversation && (
            <>
              <DialogHeader>
                <div className={cn(
                  'flex items-center justify-between p-4 pr-12 -m-6 mb-4 rounded-t-lg',
                  selectedConversation.type === 'email' ? 'bg-blue-600 text-white' :
                  (selectedConversation.type === 'call' || selectedConversation.type === 'outbound_call') ? 'bg-green-600 text-white' :
                  selectedConversation.type === 'inbound_call' ? 'bg-teal-600 text-white' :
                  'bg-purple-600 text-white'
                )}>
                  <div className="flex items-center gap-3">
                    {(() => {
                      const Icon = getTypeIcon(selectedConversation.type)
                      return <Icon className="h-6 w-6" />
                    })()}
                    <div>
                      <DialogTitle className="text-white">
                        {getTypeLabel(selectedConversation.type)}
                      </DialogTitle>
                      <p className="text-sm opacity-90">
                        {formatDate(selectedConversation.timestamp || selectedConversation.created_at || '')} at {formatTime(selectedConversation.timestamp || selectedConversation.created_at || '')}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleShareClick}
                    className="text-white hover:bg-white/20"
                  >
                    <Share2 className="h-4 w-4 mr-2" />
                    Share
                  </Button>
                </div>
              </DialogHeader>

              <div className="space-y-6">
                {/* Lead Info */}
                {(selectedConversation.lead || selectedConversation.lead_company) && (
                  <div className="flex items-center gap-4 p-3 bg-muted rounded-lg">
                    <Building2 className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">{selectedConversation.lead?.company_name || selectedConversation.lead_company}</p>
                      {/* Show phone number - lead_phone for outbound calls, caller_phone for inbound */}
                      {(selectedConversation.lead_phone || selectedConversation.caller_phone) && (
                        <p className="text-sm text-muted-foreground">
                          {selectedConversation.lead_phone || selectedConversation.caller_phone}
                        </p>
                      )}
                      {/* Show email if available */}
                      {selectedConversation.lead_email && (
                        <p className="text-sm text-muted-foreground">{selectedConversation.lead_email}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Metadata */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {selectedConversation.sentiment && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Sentiment</p>
                      <Badge className={getSentimentColor(selectedConversation.sentiment)}>
                        {selectedConversation.sentiment}
                      </Badge>
                    </div>
                  )}
                  {(selectedConversation.intent || selectedConversation.intent_detected) && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Intent</p>
                      <p className="text-sm font-medium">{selectedConversation.intent || selectedConversation.intent_detected}</p>
                    </div>
                  )}
                  {selectedConversation.duration && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Duration</p>
                      <p className="text-sm font-medium">{formatDuration(selectedConversation.duration)}</p>
                    </div>
                  )}
                  {selectedConversation.answered_by && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Answered By</p>
                      <p className="text-sm font-medium">{selectedConversation.answered_by}</p>
                    </div>
                  )}
                </div>

                {/* Tags section hidden - needs refinement
                {(selectedConversation.type === 'call' || selectedConversation.type === 'outbound_call' || selectedConversation.type === 'inbound_call') && (
                  <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                    <p className="text-sm font-medium text-indigo-800 mb-3 flex items-center gap-2">
                      <Tag className="h-4 w-4" />
                      Tags
                    </p>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {(selectedConversation.tags && selectedConversation.tags.length > 0) ? (
                        selectedConversation.tags.map(tag => (
                          <Badge
                            key={tag}
                            variant="secondary"
                            className="bg-indigo-100 text-indigo-800 pr-1 flex items-center gap-1"
                          >
                            {tag}
                            <button
                              onClick={() => handleRemoveTag(tag)}
                              className="ml-1 hover:bg-indigo-200 rounded-full p-0.5"
                              disabled={removeTagMutation.isPending}
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))
                      ) : (
                        <span className="text-sm text-muted-foreground">No tags yet</span>
                      )}
                    </div>
                    <div className="relative">
                      <div className="flex gap-2">
                        <Input
                          placeholder="Add a tag..."
                          value={newTagInput}
                          onChange={(e) => {
                            setNewTagInput(e.target.value)
                            setShowTagSuggestions(e.target.value.trim().length > 0)
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault()
                              handleAddTag()
                            } else if (e.key === 'Escape') {
                              setShowTagSuggestions(false)
                            }
                          }}
                          className="flex-1"
                        />
                        <Button
                          size="sm"
                          onClick={handleAddTag}
                          disabled={!newTagInput.trim() || addTagMutation.isPending}
                          className="bg-indigo-600 hover:bg-indigo-700"
                        >
                          {addTagMutation.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Plus className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                      {showTagSuggestions && tagSuggestions.length > 0 && (
                        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                          {tagSuggestions.map(tag => (
                            <div
                              key={tag}
                              className="px-3 py-2 text-sm cursor-pointer hover:bg-indigo-50"
                              onClick={() => {
                                setNewTagInput(tag)
                                setShowTagSuggestions(false)
                              }}
                            >
                              {tag}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                */}

                {/* Subject */}
                {selectedConversation.subject && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Subject</p>
                    <p className="font-medium">{selectedConversation.subject}</p>
                  </div>
                )}

                {/* Summary - API returns 'summary' not 'content' */}
                {selectedConversation.summary && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Summary</p>
                    <div className="p-4 bg-muted rounded-lg text-sm whitespace-pre-wrap">
                      {selectedConversation.summary}
                    </div>
                  </div>
                )}

                {/* Recording with Waveform Player - positioned right after summary for visibility */}
                {selectedConversation.recording_url && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Recording</p>
                    <AudioWavePlayer src={selectedConversation.recording_url} />
                  </div>
                )}

                {/* Transcript - API returns string, not array */}
                {selectedConversation.transcript && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Transcript</p>
                    <div className="p-4 bg-muted rounded-lg text-sm whitespace-pre-wrap max-h-64 overflow-y-auto">
                      {selectedConversation.transcript}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Email Queue Review/Edit Modal */}
      <Dialog open={!!selectedQueueItem} onOpenChange={closeModals}>
        <DialogContent className={selectedQueueItem?.status === 'pending_review' ? 'max-w-4xl max-h-[90vh] overflow-y-auto' : ''}>
          <DialogHeader>
            <div className={cn(
              "flex items-center gap-3 p-4 -m-6 mb-4 rounded-t-lg text-white",
              selectedQueueItem?.status === 'pending_review' ? 'bg-orange-600' : 'bg-blue-600'
            )}>
              {selectedQueueItem?.status === 'pending_review' ? (
                <Eye className="h-6 w-6" />
              ) : (
                <Edit2 className="h-6 w-6" />
              )}
              <DialogTitle className="text-white">
                {selectedQueueItem?.status === 'pending_review' ? 'Review Email Before Sending' : 'Email Details'}
              </DialogTitle>
            </div>
          </DialogHeader>

          {selectedQueueItem && (
            <div className="space-y-4">
              {/* Status */}
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-2">Status</Label>
                  <div className="mt-1">{getQueueStatusBadge(selectedQueueItem.status)}</div>
                </div>
                {selectedQueueItem.source && (
                  <div>
                    <Label className="text-sm font-medium text-gray-700 mb-2">Source</Label>
                    <div className="mt-1">
                      <Badge variant="outline">{selectedQueueItem.source}</Badge>
                    </div>
                  </div>
                )}
              </div>

              {/* Error message if failed */}
              {selectedQueueItem.last_error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertCircle className="h-4 w-4 text-red-600" />
                    <Label className="text-sm font-medium text-red-700">Error</Label>
                  </div>
                  <p className="text-sm text-red-800">{selectedQueueItem.last_error}</p>
                </div>
              )}

              {/* Tabs for review items - Email Details and Call Context */}
              {selectedQueueItem.status === 'pending_review' ? (
                <Tabs defaultValue="preview" className="w-full">
                  <TabsList className="grid w-full grid-cols-3 mb-4">
                    <TabsTrigger value="preview" className="flex items-center gap-2">
                      <Eye className="h-4 w-4" />
                      Email Preview
                    </TabsTrigger>
                    <TabsTrigger value="email" className="flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      Edit Details
                    </TabsTrigger>
                    <TabsTrigger value="context" className="flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      Call Context
                    </TabsTrigger>
                  </TabsList>

                  {/* Email Preview Tab */}
                  <TabsContent value="preview" className="space-y-4">
                    <div className="border rounded-lg overflow-hidden">
                      {/* Email Header */}
                      <div className="bg-gray-50 p-4 border-b space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-500 w-16">To:</span>
                          <span className="text-sm font-medium">{selectedQueueItem.to_email}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-500 w-16">Subject:</span>
                          <span className="text-sm font-medium">{selectedQueueItem.subject}</span>
                        </div>
                      </div>
                      {/* Email Body */}
                      <div className="p-4 bg-white max-h-96 overflow-y-auto">
                        {reviewItem?.body_html ? (
                          <div
                            className="prose prose-sm max-w-none"
                            dangerouslySetInnerHTML={{ __html: reviewItem.body_html }}
                          />
                        ) : reviewItem?.body_text ? (
                          <pre className="whitespace-pre-wrap font-sans text-sm">{reviewItem.body_text}</pre>
                        ) : (
                          <div className="text-center py-8 text-gray-500">
                            <Mail className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                            <p className="text-sm">Email content will be generated when approved.</p>
                            <p className="text-xs mt-1 text-gray-400">Based on company: {String(selectedQueueItem.template_data?.company_name || selectedQueueItem.lead_company || '')}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </TabsContent>

                  {/* Email Details Tab */}
                  <TabsContent value="email" className="space-y-4">
                    {/* To Email */}
                    <div>
                      <Label htmlFor="edit-to-email">To Email *</Label>
                      <Input
                        id="edit-to-email"
                        type="email"
                        value={editForm.to_email}
                        onChange={(e) => setEditForm(prev => ({ ...prev, to_email: e.target.value }))}
                      />
                    </div>

                    {/* Subject */}
                    <div>
                      <Label htmlFor="edit-subject">Subject</Label>
                      <Input
                        id="edit-subject"
                        value={editForm.subject}
                        onChange={(e) => setEditForm(prev => ({ ...prev, subject: e.target.value }))}
                      />
                    </div>

                    {/* Contact & Company */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="edit-contact">Contact Name</Label>
                        <Input
                          id="edit-contact"
                          value={editForm.contact_name}
                          onChange={(e) => setEditForm(prev => ({ ...prev, contact_name: e.target.value }))}
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit-company">Company Name</Label>
                        <Input
                          id="edit-company"
                          value={editForm.company_name}
                          onChange={(e) => setEditForm(prev => ({ ...prev, company_name: e.target.value }))}
                        />
                      </div>
                    </div>

                    {/* Review Notes */}
                    <div>
                      <Label htmlFor="review-notes">Review Notes (optional)</Label>
                      <Textarea
                        id="review-notes"
                        placeholder="Add notes about your review decision..."
                        value={reviewNotes}
                        onChange={(e) => setReviewNotes(e.target.value)}
                        className="mt-1"
                        rows={2}
                      />
                    </div>

                    {/* Info */}
                    <div className="p-3 bg-gray-50 rounded text-sm space-y-1">
                      <p><strong>Email Type:</strong> {selectedQueueItem.email_type}</p>
                      <p><strong>Attempts:</strong> {selectedQueueItem.attempts} / {selectedQueueItem.max_attempts}</p>
                      <p><strong>Created:</strong> {selectedQueueItem.created_at ? formatDate(selectedQueueItem.created_at) : '-'}</p>
                    </div>
                  </TabsContent>

                  {/* Call Details Tab */}
                  <TabsContent value="context" className="space-y-4">
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-4">
                      <p className="text-sm text-blue-700">
                        Listen to the recording and review the transcript to verify the email address collected during the call.
                      </p>

                      {reviewItem?.call_context ? (
                        <>
                          {/* Recording Player */}
                          {reviewItem.call_context.recording_url && (
                            <div>
                              <Label className="text-sm text-blue-700 mb-2 block">Call Recording</Label>
                              <AudioWavePlayer src={reviewItem.call_context.recording_url} />
                            </div>
                          )}

                          {/* Summary */}
                          {reviewItem.call_context.summary && (
                            <div>
                              <Label className="text-sm text-blue-700 mb-1 block">Call Summary</Label>
                              <p className="text-sm text-gray-700 bg-white p-3 rounded border">
                                {reviewItem.call_context.summary}
                              </p>
                            </div>
                          )}

                          {/* Transcript */}
                          {reviewItem.call_context.transcript && (
                            <div>
                              <Label className="text-sm text-blue-700 mb-1 block">Full Transcript</Label>
                              <div className="text-sm text-gray-700 bg-white p-3 rounded border max-h-64 overflow-y-auto whitespace-pre-wrap font-mono">
                                {reviewItem.call_context.transcript}
                              </div>
                            </div>
                          )}

                          {/* Transcript excerpt if full transcript not available */}
                          {!reviewItem.call_context.transcript && reviewItem.transcript_excerpt && (
                            <div>
                              <Label className="text-sm text-blue-700 mb-1 block">Transcript Excerpt</Label>
                              <div className="text-sm text-gray-700 bg-white p-3 rounded border whitespace-pre-wrap font-mono">
                                {reviewItem.transcript_excerpt}
                              </div>
                            </div>
                          )}

                          {!reviewItem.call_context.recording_url && !reviewItem.call_context.transcript && !reviewItem.transcript_excerpt && (
                            <p className="text-sm text-gray-500 italic">No call recording or transcript available for this email.</p>
                          )}
                        </>
                      ) : reviewItem?.transcript_excerpt ? (
                        /* Show transcript excerpt even without full call_context */
                        <div>
                          <Label className="text-sm text-blue-700 mb-1 block">Transcript Excerpt</Label>
                          <div className="text-sm text-gray-700 bg-white p-3 rounded border whitespace-pre-wrap font-mono">
                            {reviewItem.transcript_excerpt}
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <Phone className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-sm text-gray-500">
                            Call details not available for this email request.
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            This may be an older request or the call data is still processing.
                          </p>
                        </div>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
              ) : (
                /* Non-review items - show simple form without tabs */
                <>
                  {/* Editable Email Fields */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-800">Email Details</h4>

                    {/* To Email */}
                    <div>
                      <Label htmlFor="edit-to-email">To Email *</Label>
                      <Input
                        id="edit-to-email"
                        type="email"
                        value={editForm.to_email}
                        onChange={(e) => setEditForm(prev => ({ ...prev, to_email: e.target.value }))}
                      />
                    </div>

                    {/* Subject */}
                    <div>
                      <Label htmlFor="edit-subject">Subject</Label>
                      <Input
                        id="edit-subject"
                        value={editForm.subject}
                        onChange={(e) => setEditForm(prev => ({ ...prev, subject: e.target.value }))}
                      />
                    </div>

                    {/* Contact & Company */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="edit-contact">Contact Name</Label>
                        <Input
                          id="edit-contact"
                          value={editForm.contact_name}
                          onChange={(e) => setEditForm(prev => ({ ...prev, contact_name: e.target.value }))}
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit-company">Company Name</Label>
                        <Input
                          id="edit-company"
                          value={editForm.company_name}
                          onChange={(e) => setEditForm(prev => ({ ...prev, company_name: e.target.value }))}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Info */}
                  <div className="p-3 bg-gray-50 rounded text-sm space-y-1">
                    <p><strong>Email Type:</strong> {selectedQueueItem.email_type}</p>
                    <p><strong>Attempts:</strong> {selectedQueueItem.attempts} / {selectedQueueItem.max_attempts}</p>
                    <p><strong>Created:</strong> {selectedQueueItem.created_at ? formatDate(selectedQueueItem.created_at) : '-'}</p>
                  </div>
                </>
              )}
            </div>
          )}

          <DialogFooter className="flex gap-2 flex-wrap">
            {/* Pending Review Actions */}
            {selectedQueueItem?.status === 'pending_review' && (
              <>
                {/* Refresh and AI Refine buttons */}
                <Button
                  variant="outline"
                  onClick={() => refreshTemplateMutation.mutate(selectedQueueItem.id)}
                  disabled={refreshTemplateMutation.isPending || aiRefineMutation.isPending}
                  title="Regenerate email using the current active template"
                >
                  {refreshTemplateMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <RotateCcw className="h-4 w-4 mr-2" />
                  )}
                  Refresh Template
                </Button>
                <Button
                  variant="outline"
                  onClick={() => aiRefineMutation.mutate({ id: selectedQueueItem.id })}
                  disabled={aiRefineMutation.isPending || refreshTemplateMutation.isPending}
                  className="text-purple-600 border-purple-200 hover:bg-purple-50"
                  title="Use AI to personalize the email based on the call transcript"
                >
                  {aiRefineMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Sparkles className="h-4 w-4 mr-2" />
                  )}
                  AI Refine
                </Button>
                <div className="flex-1" /> {/* Spacer to push action buttons to the right */}
                <Button
                  variant="outline"
                  onClick={handleReject}
                  disabled={rejectMutation.isPending}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  {rejectMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <XCircle className="h-4 w-4 mr-2" />
                  )}
                  Reject
                </Button>
                <Button
                  onClick={handleApprove}
                  disabled={approveMutation.isPending}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {approveMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                  )}
                  Approve & Send
                </Button>
              </>
            )}

            {/* Failed/Cancelled Actions - Retry */}
            {selectedQueueItem && ['failed', 'cancelled'].includes(selectedQueueItem.status) && (
              <Button
                onClick={handleRetry}
                disabled={retryMutation.isPending}
              >
                {retryMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Retry
              </Button>
            )}

            {/* Sent/Processing (actually sent) Actions - Resend */}
            {selectedQueueItem && (selectedQueueItem.status === 'sent' || (selectedQueueItem.status === 'processing' && selectedQueueItem.sent_at)) && (
              <Button
                onClick={handleResend}
                disabled={resendMutation.isPending}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {resendMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Resend Email
              </Button>
            )}

            {/* Pending/Processing Actions - Cancel */}
            {selectedQueueItem && ['pending', 'processing'].includes(selectedQueueItem.status) && !selectedQueueItem.sent_at && (
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={cancelMutation.isPending}
              >
                {cancelMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <XCircle className="h-4 w-4 mr-2" />
                )}
                Cancel
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Conversation Dialog */}
      <Dialog open={showShareDialog} onOpenChange={(open) => {
        setShowShareDialog(open)
        if (!open) {
          setShareEmail('')
          setShareMessage('')
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3 p-4 -m-6 mb-4 rounded-t-lg bg-indigo-600 text-white">
              <Share2 className="h-6 w-6" />
              <DialogTitle className="text-white">Share Conversation</DialogTitle>
            </div>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="share-email">Recipient Email</Label>
              <Input
                id="share-email"
                type="email"
                placeholder="Enter email address"
                value={shareEmail}
                onChange={(e) => setShareEmail(e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor="share-message">Message (optional)</Label>
              <Textarea
                id="share-message"
                placeholder="Add a personal message..."
                value={shareMessage}
                onChange={(e) => setShareMessage(e.target.value)}
                rows={3}
              />
            </div>

            {shareMutation.isError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                Failed to share conversation. Please try again.
              </div>
            )}

            {shareMutation.isSuccess && (
              <div className="p-3 bg-green-50 border border-green-200 rounded text-green-700 text-sm">
                Conversation shared successfully!
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowShareDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleShareSubmit}
              disabled={!shareEmail || shareMutation.isPending}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {shareMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default Conversations
